import { isDefined } from "@clipboard-health/util-ts";

import { type PlacementWithDetails } from "../../../api/useFetchPaginatedPlacements";

interface AlternativeAddressProps {
  interviewSettings?: PlacementWithDetails["interviewSettings"];
  placementAddressFormatted?: string;
}

export interface AlternativeAddressData {
  coordinates: {
    longitude: number;
    latitude: number;
  };
  distance: number;
  formattedAddress: string;
  description: string;
}

interface AlternativeAddressResult {
  showAlternativeAddressInformation: boolean;
  showBookedInterviewLocation: boolean;
  alternativeAddressData?: AlternativeAddressData;
}

export function useAlternativeAddress(props: AlternativeAddressProps): AlternativeAddressResult {
  const { interviewSettings, placementAddressFormatted } = props;

  if (!isDefined(placementAddressFormatted)) {
    return getBaseResult();
  }

  const interviewAddress = interviewSettings?.attributes.interviewAddress;
  const interviewAddressEnabled =
    isDefined(interviewAddress) && Boolean(interviewSettings?.attributes.scheduleInterviews);

  if (interviewAddressEnabled) {
    return getAddressFromInterviewAddress({
      interviewAddress,
      placementAddressFormatted,
      interviewSettings,
    });
  }

  return getBaseResult();
}

function getBaseResult(): AlternativeAddressResult {
  return {
    showAlternativeAddressInformation: false,
    showBookedInterviewLocation: false,
  };
}

function getAddressFromInterviewAddress(params: {
  interviewAddress: NonNullable<
    NonNullable<PlacementWithDetails["interviewSettings"]>["attributes"]["interviewAddress"]
  >;
  placementAddressFormatted: string;
  interviewSettings: PlacementWithDetails["interviewSettings"];
}): AlternativeAddressResult {
  const { interviewAddress, placementAddressFormatted, interviewSettings } = params;
  const hasDifferentAddress = interviewAddress.address.formatted !== placementAddressFormatted;
  return {
    showAlternativeAddressInformation: hasDifferentAddress,
    showBookedInterviewLocation: hasDifferentAddress,
    alternativeAddressData: {
      coordinates: interviewAddress.coordinates,
      distance: interviewSettings?.attributes.interviewAddressDistance ?? 0,
      formattedAddress: interviewAddress.address.formatted,
      description: interviewSettings?.attributes.interviewAddressDescription ?? "",
    },
  };
}
